import { z } from 'zod';

import type { UserTagLiteral } from './user';

/**
 * Generated voxel avatars.
 *
 * Every user and space carries a `robohash` column. The name is historical —
 * it once held a seed for robohash.org — and it is deliberately unchanged: the
 * string is opaque, it appears in ~470 places across the Prisma schema, the
 * tRPC wire types, the JWT payload, the socket payloads, and the on-device
 * SQLite mirror, and renaming it would buy nothing but churn.
 *
 * What the column now holds is an **avatar spec**: a seed, optionally followed
 * by a `?`-separated query string of style choices.
 *
 *   `9f3c…`                        → the seed alone, style picked from the seed
 *   `9f3c…?top=afro&eyes=happy`    → the seed plus explicit choices
 *
 * The spec is the whole identity of the picture, so it is also the whole cache
 * key: change a choice and the URL changes, which retires the old immutable
 * cache entry without any invalidation step. See `helpers/avatarUrl.ts`.
 *
 * Only a verified human may store choices — see `users.updateUser`.
 * Everyone else renders from the bare seed, on the brand background.
 */

/**
 * The component variants Voxel Art ships, copied from
 * `apps/server/src/data/dicebear/voxel-art.min.json`.
 *
 * They are duplicated here rather than read from the definition because the
 * customizer runs on the client, which has no reason to ship 138 KB of vector
 * art just to list the names. `avatarRender.test.ts` asserts the two agree, so
 * the copy cannot drift silently.
 *
 * `animation` is omitted on purpose: it is a motion setting, not an appearance
 * one, and an avatar that moves in a feed of two hundred avatars is a
 * distraction rather than a feature.
 */
export const AVATAR_VARIANTS = {
  top: [
    'short',
    'spiky',
    'bowl',
    'sideSwept',
    'curly',
    'mohawk',
    'buns',
    'ponytail',
    'bob',
    'shoulderLength',
    'longStraight',
    'longWavy',
    'partedLong',
    'braids',
    'twinTails',
    'cap',
    'beanie',
    'animalEars',
    'bunnyEars',
    'afro',
    'halfShaved'
  ],
  eyes: ['open', 'soft', 'happy', 'sleepy', 'side', 'closed', 'wide', 'star'],
  eyebrows: ['flat', 'raised', 'angry', 'soft'],
  mouth: [
    'smile',
    'bigSmile',
    'flat',
    'ooh',
    'tongue',
    'smirk',
    'laugh',
    'wideSmile',
    'frown',
    'grin'
  ],
  nose: ['block', 'wide', 'small', 'tall'],
  cheeks: ['blush', 'pixel', 'freckles'],
  beard: ['full', 'mustache', 'goatee', 'stubble'],
  glasses: ['round', 'square', 'shades', 'cat', 'visor'],
  outfit: [
    'plain',
    'stripes',
    'jacket',
    'checker',
    'overalls',
    'hoodie',
    'tie',
    'coat',
    'dress',
    'suit'
  ]
} as const satisfies Record<string, readonly string[]>;

/**
 * The colour palettes Voxel Art ships, same source and same drift test as
 * {@link AVATAR_VARIANTS}. Values are lowercase hex **without** the leading
 * `#`, which is the form the spec query string carries — a `#` in a URL starts
 * a fragment, and encoding it on every read and write is a bug waiting to
 * happen. The renderer adds the `#` back.
 *
 * `background` is excluded: Voxel Art declares the palette but never draws it
 * (the figure renders on transparency), and the background is ours — see
 * `renderAvatarSvg`.
 */
export const AVATAR_PALETTES = {
  skin: [
    'f5d0b0',
    'eab890',
    'dda878',
    'c99062',
    'b07347',
    '95562f',
    '7d4a26',
    '6a3d1f'
  ],
  hair: [
    '2c222b',
    '3b2f2f',
    '5a3825',
    '7b4a2d',
    'a56b46',
    'c98850',
    'd9b380',
    'e8d4a8',
    'b55239',
    'd6455d',
    '6d5acf',
    '3fb27f'
  ],
  hat: [
    'c68a5f',
    'f2f2f2',
    '8d95d6',
    'e0876d',
    '5ba8a0',
    'd9a03c',
    'a06fb8',
    '4a6fa5'
  ],
  shirt: [
    'e64980',
    'f76707',
    'fab005',
    '40c057',
    '12b886',
    '228be6',
    '4c6ef5',
    '7950f2',
    'e8590c',
    '495057'
  ],
  jacket: ['f8f9fa', '343a40', '3b5b8c', '6b4f3a', 'b55239', '5ba8a0'],
  pants: ['3b5b8c', '2f4160', '4a4e69', '6b4f3a', '495057', '7a86a8'],
  shoes: ['f1f3f5', '343a40', 'd6336c', '1971c2', 'f08c00', '6741d9']
} as const satisfies Record<string, readonly string[]>;

export type AvatarVariantName = keyof typeof AVATAR_VARIANTS;
export type AvatarPaletteName = keyof typeof AVATAR_PALETTES;

/**
 * Components DiceBear draws only some of the time — `beard` at 20%, `glasses`
 * at 15%, and so on. A customizer needs to offer "none" for these, which the
 * spec expresses by forcing the component's probability to 0 rather than by
 * naming a variant. Anything not listed here is always drawn, so "none" is not
 * an option for it.
 */
export const AVATAR_OPTIONAL_VARIANTS = [
  'beard',
  'glasses',
  'cheeks',
  'eyebrows',
  'top'
] as const satisfies readonly AvatarVariantName[];

/** The literal a spec uses to switch an optional component off. */
export const AVATAR_VARIANT_NONE = 'none';

const variantSchema = (name: AvatarVariantName) => {
  const values = AVATAR_VARIANTS[name] as readonly string[];
  const allowed = (AVATAR_OPTIONAL_VARIANTS as readonly string[]).includes(name)
    ? [...values, AVATAR_VARIANT_NONE]
    : values;
  // `z.enum` needs a non-empty tuple; every palette above has entries, so the
  // cast only satisfies the type, it never widens what is accepted.
  return z.enum(allowed as [string, ...string[]]);
};

const paletteSchema = (name: AvatarPaletteName) =>
  z.enum(AVATAR_PALETTES[name] as unknown as [string, ...string[]]);

/**
 * Every choice a spec may carry, all optional — an omitted key means "let the
 * seed decide", which is what an uncustomised avatar does for all of them.
 *
 * `.strict()` matters more than usual here. The spec arrives as a URL query
 * string on a public, unauthenticated endpoint, and DiceBear accepts a much
 * wider option surface than we expose (`scale`, `rotate`, `flip`, per-component
 * probabilities, arbitrary colour arrays). Passing an unvetted object straight
 * through would let a stranger render a caller's avatar rotated and scaled off
 * the canvas, and would make the cache key space unbounded. Strict parsing
 * rejects the whole spec instead, and the endpoint falls back to the seed.
 */
export const avatarOptionsSchema = z
  .object({
    top: variantSchema('top').optional(),
    eyes: variantSchema('eyes').optional(),
    eyebrows: variantSchema('eyebrows').optional(),
    mouth: variantSchema('mouth').optional(),
    nose: variantSchema('nose').optional(),
    cheeks: variantSchema('cheeks').optional(),
    beard: variantSchema('beard').optional(),
    glasses: variantSchema('glasses').optional(),
    outfit: variantSchema('outfit').optional(),
    skin: paletteSchema('skin').optional(),
    hair: paletteSchema('hair').optional(),
    hat: paletteSchema('hat').optional(),
    shirt: paletteSchema('shirt').optional(),
    jacket: paletteSchema('jacket').optional(),
    pants: paletteSchema('pants').optional(),
    shoes: paletteSchema('shoes').optional()
  })
  .strict();

export type AvatarOptions = z.infer<typeof avatarOptionsSchema>;

/**
 * The pixel sizes the server will rasterize an avatar to.
 *
 * React Native's `Image` decodes PNG, JPEG, GIF and WebP — not SVG — so mobile
 * cannot use the vector the web renders and asks for pixels instead. The list
 * is short and closed on purpose: size is a cache axis on a public endpoint, so
 * an open one multiplies the cache by however many integers a caller cares to
 * try, and an uncapped one invites a request for an 8192px render. Three
 * buckets cover every avatar the app draws, at 1x through 3x density.
 */
export const AVATAR_PNG_SIZES = [128, 256, 512] as const;
export type AvatarPngSize = (typeof AVATAR_PNG_SIZES)[number];

export const DEFAULT_AVATAR_PNG_SIZE: AvatarPngSize = 256;

export const isAvatarPngSize = (size: number): size is AvatarPngSize =>
  (AVATAR_PNG_SIZES as readonly number[]).includes(size);

/**
 * Picks the bucket to request for an avatar drawn at `cssSize` points on a
 * screen of `pixelRatio` device pixels per point.
 *
 * Rounds up, so an avatar is never upscaled from too few pixels, and clamps to
 * the largest bucket rather than failing — a 200pt avatar on a 3x screen wants
 * 600px and gets 512, which is a slightly soft avatar instead of no avatar.
 */
export const pickAvatarPngSize = (
  cssSize: number,
  pixelRatio: number
): AvatarPngSize => {
  const wanted = cssSize * pixelRatio;
  return (
    AVATAR_PNG_SIZES.find((size) => size >= wanted) ??
    AVATAR_PNG_SIZES[AVATAR_PNG_SIZES.length - 1]
  );
};

/**
 * Whether to offer this account the avatar customizer.
 *
 * The server's rule is `verifiedHumanAt !== null` (see `users.updateUser`), and
 * that field is not on the details cookie the clients read — adding it would
 * change a payload that mobile builds already in the wild parse with a fixed
 * schema. `HUMAN` is the exact proxy for it in practice: `onBiometricVerified`
 * is the only writer of `verifiedHumanAt`, and it stamps the tag in the same
 * update.
 *
 * This decides what the UI *shows*. The server decides what it *accepts*, so a
 * proxy that drifts costs a confused user, never an unearned avatar.
 */
export const canCustomizeAvatar = (tag: UserTagLiteral): boolean =>
  tag === 'HUMAN';

/**
 * The customizer's layout: which groups it shows, in what order, under what
 * name. Shared so the web panel and the mobile sheet cannot drift into
 * offering different features or calling one thing two names.
 *
 * Order is roughly head-down and inside-out — the things a person recognises
 * themselves by first (skin, hair, face) ahead of the things they change most
 * often (clothes).
 */
export const AVATAR_EDITOR_GROUPS = [
  { kind: 'palette', name: 'skin', label: 'Skin' },
  { kind: 'variant', name: 'top', label: 'Hair' },
  { kind: 'palette', name: 'hair', label: 'Hair colour' },
  { kind: 'variant', name: 'eyes', label: 'Eyes' },
  { kind: 'variant', name: 'eyebrows', label: 'Eyebrows' },
  { kind: 'variant', name: 'nose', label: 'Nose' },
  { kind: 'variant', name: 'mouth', label: 'Mouth' },
  { kind: 'variant', name: 'cheeks', label: 'Cheeks' },
  { kind: 'variant', name: 'beard', label: 'Beard' },
  { kind: 'variant', name: 'glasses', label: 'Glasses' },
  { kind: 'variant', name: 'outfit', label: 'Outfit' },
  { kind: 'palette', name: 'shirt', label: 'Shirt' },
  { kind: 'palette', name: 'jacket', label: 'Jacket' },
  { kind: 'palette', name: 'pants', label: 'Trousers' },
  { kind: 'palette', name: 'shoes', label: 'Shoes' },
  { kind: 'palette', name: 'hat', label: 'Hat' }
] as const satisfies readonly (
  | { kind: 'variant'; name: AvatarVariantName; label: string }
  | { kind: 'palette'; name: AvatarPaletteName; label: string }
)[];

export type AvatarEditorGroup = (typeof AVATAR_EDITOR_GROUPS)[number];

/**
 * The choices a group offers, including the `none` entry for a component that
 * is only drawn some of the time. `undefined` is not in the list: "let the seed
 * decide" is the state of an option that has never been set, and the customizer
 * expresses it with its Reset control rather than as a per-group choice.
 */
export const avatarGroupChoices = (
  group: AvatarEditorGroup
): readonly string[] => {
  if (group.kind === 'palette') return AVATAR_PALETTES[group.name];
  const values = AVATAR_VARIANTS[group.name] as readonly string[];
  return (AVATAR_OPTIONAL_VARIANTS as readonly string[]).includes(group.name)
    ? [AVATAR_VARIANT_NONE, ...values]
    : values;
};

/**
 * Seeds are UUIDs today (the Prisma column defaults to `uuid()`), but older
 * rows and the kitchen-sink fixtures use short words, so the guard is a shape
 * and a length rather than a UUID check. The cap is what stops a caller from
 * filling the render cache with megabyte-long keys.
 */
const SEED_MAX_LENGTH = 64;
const SEED_PATTERN = /^[\w.:-]{1,64}$/;

export const isValidAvatarSeed = (seed: string): boolean =>
  seed.length <= SEED_MAX_LENGTH && SEED_PATTERN.test(seed);

/**
 * Generates a fresh seed — what "give me a different avatar" produces.
 *
 * `crypto.randomUUID` needs a secure context, which the app is not always in:
 * the dev server runs over plain HTTP on a LAN address. The fallback is not
 * cryptographic and does not need to be — a seed is a public label picking a
 * cartoon, so a collision between two users is cosmetic, and the Prisma column
 * default (`uuid()`) is what assigns the one a real account starts with.
 */
export const newAvatarSeed = (): string => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * What `users.updateUser` takes as `avatarId` to mean "remove the uploaded
 * picture", as distinct from `undefined`, which means "leave it alone".
 *
 * A sentinel rather than `null` because the field already uses `null` for
 * "unchanged" in the places that build the payload, and flipping that meaning
 * would have to be done everywhere at once. Shared so the client and the server
 * cannot disagree about the exact string — the server has understood this value
 * for some time, and the client never sent it, which is why clearing an avatar
 * silently did nothing.
 */
export const REMOVE_AVATAR = '__removeAvatarNULL';

/**
 * Splits a stored spec into its seed and its (validated) choices.
 *
 * Never throws: a spec that fails validation degrades to its seed with no
 * choices, because a broken avatar is worse than a plain one. `valid` reports
 * which happened, so the endpoint can answer 400 for a hand-typed URL while the
 * renderer just carries on.
 */
export const decodeAvatarSpec = (
  spec: string
): { seed: string; options: AvatarOptions; valid: boolean } => {
  const separator = spec.indexOf('?');
  const seed = separator === -1 ? spec : spec.slice(0, separator);
  const query = separator === -1 ? '' : spec.slice(separator + 1);

  if (!isValidAvatarSeed(seed)) return { seed, options: {}, valid: false };
  if (!query) return { seed, options: {}, valid: true };

  const parsed = avatarOptionsSchema.safeParse(
    Object.fromEntries(new URLSearchParams(query))
  );
  return parsed.success
    ? { seed, options: parsed.data, valid: true }
    : { seed, options: {}, valid: false };
};

/**
 * The inverse of {@link decodeAvatarSpec}. Keys are emitted in a fixed order so
 * the same choices always produce the same string — two specs that differ only
 * in key order would otherwise be two cache entries for one picture, and two
 * "different" values for an equality check.
 */
export const encodeAvatarSpec = (
  seed: string,
  options: AvatarOptions
): string => {
  const query = new URLSearchParams();
  for (const key of Object.keys(
    avatarOptionsSchema.shape
  ) as (keyof AvatarOptions)[]) {
    const value = options[key];
    if (value !== undefined) query.set(key, value);
  }
  const encoded = query.toString();
  return encoded ? `${seed}?${encoded}` : seed;
};
